import React, { useState } from "react";
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Stack,
  FormControl,
  FormLabel,
  Select,
  Option,
  Textarea,
  Button,
  Box,
  CircularProgress,
} from "@mui/joy";
import MDEditor from '@uiw/react-md-editor';
import { generateApplicantSummary } from "apiconfig/api";

interface AISummaryModalProps {
  open: boolean;
  onClose: () => void;
  onUseSummary: (summary: string) => void;
  userUuid: string;
}

const AISummaryModal: React.FC<AISummaryModalProps> = ({
  open,
  onClose,
  onUseSummary,
  userUuid,
}) => {
  const [modelName, setModelName] = useState("o4-mini");
  const [prompt, setPrompt] = useState(
    `Create a summary of this job applicant from the attached transcript. The summary should be lightly formatted with markdown and have three sections (exact titles in bold/**) as described below. Please avoid the use of gender-specific pronouns like he, his, she, hers. Instead use they/them/their, or structure your sentences so that pronouns aren't needed.

**QUALIFIED FOR:**
List the best 1 or 2 or 3 industry standard roles a candidate is suited for in the restaurant industry. If any explicit verbal mention of a role or multiple roles, heavily favor those in your determination.

**PAST EXPERIENCE:**
In 75 or fewer words, summarize this candidate's past experience, mentioning specific jobs when possible.

**WHY THIS JOB?**
In 75 or fewer words, summarize why this person specifically wants the job at the employer to which they're applying.`
  );
  const [generatedSummary, setGeneratedSummary] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    
    try {
      const response = await generateApplicantSummary(userUuid, {
        modelName,
        prompt,
      });
      
      if (response.success && response.data?.summary) {
        setGeneratedSummary(response.data.summary);
      } else {
        setError("Failed to generate summary. Please try again.");
      }
    } catch (err: any) {
      console.error("Error generating summary:", err);
      setError(err?.message || "Failed to generate summary. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUseSummary = () => {
    if (generatedSummary) {
      onUseSummary(generatedSummary);
      handleClose();
    }
  };

  const handleRegenerate = () => {
    setGeneratedSummary(null);
    setError(null);
  };

  const handleClose = () => {
    setGeneratedSummary(null);
    setError(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog
        sx={{
          minWidth: { xs: "90%", sm: "600px", md: "700px" },
          maxWidth: "800px",
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <ModalClose />
        <Typography level="h4" sx={{ mb: 2, fontWeight: 600 }}>
          Generate Summary with AI
        </Typography>

        {!generatedSummary ? (
          <Stack spacing={2}>
            <FormControl>
              <FormLabel>Model</FormLabel>
              <Select
                value={modelName}
                onChange={(_, value) => setModelName(value as string)}
                disabled={isGenerating}
              >
                <Option value="o4-mini">o4-mini</Option>
                <Option value="gpt-4">GPT-4</Option>
                <Option value="gpt-5">GPT-5</Option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Prompt</FormLabel>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                minRows={3}
                maxRows={6}
                disabled={isGenerating}
                placeholder="Enter your prompt..."
              />
            </FormControl>

            {error && (
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "#FEE",
                  borderRadius: "8px",
                  border: "1px solid #FCC",
                }}
              >
                <Typography level="body-sm" sx={{ color: "#C00" }}>
                  {error}
                </Typography>
              </Box>
            )}

            <Button
              onClick={handleGenerate}
              loading={isGenerating}
              fullWidth
              sx={{ mt: 2 }}
            >
              {isGenerating ? "Generating..." : "Generate"}
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Typography level="body-sm" sx={{ color: "#666", mb: 1 }}>
              Generated Summary:
            </Typography>
            
            <Box
              data-color-mode="light"
              sx={{
                border: "1px solid #CDD7E1",
                borderRadius: "8px",
                overflow: "hidden",
                '& .wmde-markdown': {
                  backgroundColor: '#F8F9FA',
                  padding: '16px',
                  '& ul': {
                    listStyleType: 'disc',
                    listStylePosition: 'outside',
                    paddingLeft: '1.5em',
                  },
                  '& ol': {
                    listStyleType: 'decimal',
                    listStylePosition: 'outside',
                    paddingLeft: '1.5em',
                  },
                  '& li': {
                    display: 'list-item',
                  },
                },
              }}
            >
              <MDEditor.Markdown source={generatedSummary} />
            </Box>

            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                color="neutral"
                onClick={handleRegenerate}
                fullWidth
              >
                Regenerate
              </Button>
              <Button
                variant="solid"
                color="primary"
                onClick={handleUseSummary}
                fullWidth
              >
                Use this summary
              </Button>
            </Stack>
          </Stack>
        )}
      </ModalDialog>
    </Modal>
  );
};

export default AISummaryModal;

